// ─────────────────────────────────────────────────────────────────────
//  Epoint.az payment gateway — server-side signing flow
// ─────────────────────────────────────────────────────────────────────
//  Matches the official WooCommerce/OpenCart plugin contract:
//    data       = base64( json_encode(payload) )
//    signature  = base64( sha1_raw( private_key + data + private_key ) )
//
//  The browser sends the merchant keys + payload to our FastAPI backend,
//  which performs signing + the actual POST to epoint.az server-side.
//  This keeps the private_key out of the client bundle, dodges browser
//  CORS, and guarantees byte-perfect JSON (Python's json.dumps with
//  separators=(",", ":") matches PHP's json_encode used by the official
//  plugin so the signature is always accepted).
//
//  Backend endpoints (see /app/backend/server.py):
//    POST /api/epoint/create-payment   → card redirect flow
//    POST /api/epoint/widget-url       → Apple Pay / Google Pay iframe
//    POST /api/epoint/get-status       → server-to-server status check
//    POST /api/epoint/verify-callback  → verify redirect-back signature
// ─────────────────────────────────────────────────────────────────────

import axios from "axios";
import { getEpointConfig } from "./firestore";

// Default fallback for the user-facing site domain. Used ONLY if the page is
// being rendered in a context with no `window.location` (server-side render)
// AND no admin override is set in `settings/epoint.redirect_base`. In normal
// browser flow we always prefer the current `window.location.origin` so
// Epoint redirects back to the SAME host the buyer started checkout from.
// This eliminates the entire class of "domain mismatch / SSL on another host"
// bugs (e.g. preview URL vs production domain).
const DEFAULT_DOMAIN = "https://vinylbaku.az";

/**
 * Determine which base URL Epoint should redirect the buyer back to after
 * a successful or failed charge.
 *
 *   Priority:
 *     1. Admin override (settings/epoint.redirect_base)  — explicit choice
 *     2. window.location.origin                          — same-origin flow
 *     3. DEFAULT_DOMAIN                                  — last-resort
 */
function getRedirectBase(cfg) {
  const override = (cfg && typeof cfg.redirect_base === "string") ? cfg.redirect_base.trim() : "";
  if (override) return override.replace(/\/+$/, "");
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin.replace(/\/+$/, "");
  }
  return DEFAULT_DOMAIN;
}

// Same-origin Epoint endpoints. On Vercel they are served by the serverless
// functions under /app/frontend/api/epoint/*.js. On Netlify they are mapped
// by netlify.toml redirects to /.netlify/functions/epoint-api. In local dev
// (Emergent preview) there is no /api/epoint/* on the frontend host, so we
// fall back to the FastAPI backend via REACT_APP_BACKEND_URL.
//
// Detection order:
//   1. Browser → always try same-origin /api/epoint/* first (production).
//   2. If REACT_APP_BACKEND_URL is set AND points elsewhere than the page
//      origin (i.e. dev with backend on another port), use it as base.
const PAGE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const ENV_BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";

const EPOINT_BASE = (() => {
  // If REACT_APP_BACKEND_URL is configured and different from current page
  // origin (typical local-dev or Emergent preview), use it. Otherwise use
  // the page origin so serverless functions on the same host are hit.
  if (ENV_BACKEND && ENV_BACKEND.replace(/\/+$/, "") !== PAGE_ORIGIN.replace(/\/+$/, "")) {
    return `${ENV_BACKEND.replace(/\/+$/, "")}/api/epoint`;
  }
  return `${PAGE_ORIGIN.replace(/\/+$/, "")}/api/epoint`;
})();

const epx = axios.create({
  baseURL: EPOINT_BASE,
  timeout: 30000,
  headers: { "Content-Type": "application/json", Accept: "application/json" },
  // We need to detect SPA-fallback HTML responses (when Netlify Functions
  // are not deployed) and surface a clear error instead of silently
  // letting axios parse HTML as garbage data. We therefore do NOT
  // transformResponse — we let axios do its default JSON parse, and the
  // interceptor below catches the (str / object) and bails out if it
  // smells like HTML.
});

epx.interceptors.response.use(
  (resp) => {
    // If the same-origin /api/epoint/* path is not actually wired to a
    // server (typical Netlify drag-and-drop deploy with the static `build`
    // folder only — Functions not included), Netlify falls back to
    // index.html with a 200 OK. axios will hand us back the HTML string.
    // Detect this and convert into a friendly error.
    const ct = String(resp.headers?.["content-type"] || "").toLowerCase();
    const looksHtml =
      (typeof resp.data === "string" && resp.data.trim().startsWith("<")) ||
      (ct.includes("text/html"));
    if (looksHtml) {
      const e = new Error(
        "Ödəniş serveri tapılmadı. Netlify Functions deploy olunmayıb — " +
        "saytı `netlify-deploy` qovluğu ilə (build + functions birlikdə) yenidən deploy edin."
      );
      e.code = "EPOINT_FUNCTIONS_MISSING";
      throw e;
    }
    return resp;
  },
  (err) => Promise.reject(err),
);

// ─────────────────────────────────────────────────────────────────────
// Local helpers — kept for redirect-back callback verification on the
// /success and /result landing pages (no signing, just decoding).
// ─────────────────────────────────────────────────────────────────────

function utf8ToBase64(str) {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

async function sha1Base64(str) {
  const bytes = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-1", bytes);
  const hashBytes = new Uint8Array(hashBuffer);
  let binary = "";
  for (let i = 0; i < hashBytes.length; i += 1) binary += String.fromCharCode(hashBytes[i]);
  return btoa(binary);
}

/**
 * Build a signed payload locally — primarily used by tests / debug.
 * Production code paths use the server-side `create-payment` endpoint.
 */
export async function buildSignedPayload(privateKey, jsonData) {
  const json = JSON.stringify(jsonData);
  const data = utf8ToBase64(json);
  const signature = await sha1Base64(`${privateKey}${data}${privateKey}`);
  return { data, signature };
}

/**
 * Local SHA-1 verification of a redirect-back (data, signature) pair.
 * Backend `/verify-callback` is preferred — this is a no-network fallback.
 */
export async function verifySignature(privateKey, data, signature) {
  if (!privateKey || !data || !signature) return false;
  const expected = await sha1Base64(`${privateKey}${data}${privateKey}`);
  return expected === signature;
}

/**
 * Decode the base64-JSON `data` field that Epoint sends back on the
 * success / error / result URLs.
 */
export function decodeEpointData(data) {
  try {
    const decoded = atob(data);
    const bytes = new Uint8Array([...decoded].map((c) => c.charCodeAt(0)));
    const text = new TextDecoder().decode(bytes);
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Server-side flow
// ─────────────────────────────────────────────────────────────────────

async function readEpointConfig() {
  const cfg = await getEpointConfig();
  if (!cfg || !cfg.enabled) {
    throw new Error("Epoint deaktivdir. Admin paneldən aktivləşdirin.");
  }
  if (!cfg.public_key || !cfg.private_key) {
    throw new Error("Epoint açarları daxil edilməyib.");
  }
  return cfg;
}

function unwrapError(e, fallback) {
  const detail =
    e?.response?.data?.detail ||
    e?.response?.data?.message ||
    e?.message ||
    fallback;
  return new Error(String(detail));
}

/**
 * Initiate a card-entry payment with Epoint. Returns the bank `redirect_url`.
 */
export async function createEpointPayment(params) {
  const cfg = params.cfg || (await readEpointConfig());

  const amount = Number(params.amountAzn || 0);
  if (!(amount > 0)) throw new Error("Ödəniş məbləği düzgün deyil.");

  const payload = {
    public_key: cfg.public_key.trim(),
    private_key: cfg.private_key.trim(),
    amount: +amount.toFixed(2),
    order_id: String(params.orderId),
    currency: "AZN",
    language: params.language || "az",
    description: (params.description || `Sifariş ${params.orderId}`).slice(0, 250),
    success_redirect_url: params.successRedirect,
    error_redirect_url: params.errorRedirect,
  };
  if (cfg.result_url) payload.result_url = cfg.result_url;
  if (params.isInstallment) payload.is_installment = 1;

  let resp;
  try {
    const r = await epx.post("/create-payment", payload);
    resp = r.data || {};
  } catch (e) {
    throw unwrapError(e, "Epoint serveri ilə əlaqə qurula bilmədi");
  }

  if (resp.status !== "success" || !resp.redirect_url) {
    throw new Error(resp.message || "Epoint ödəniş URL-i alınmadı");
  }

  return {
    redirect_url: resp.redirect_url,
    transaction: resp.transaction || "",
    status: resp.status,
  };
}

/**
 * Fetch the Apple Pay / Google Pay iframe widget URL for the given order.
 */
export async function fetchEpointWidgetUrl({ orderId, amountAzn, description }) {
  const cfg = await readEpointConfig();

  const amount = Number(amountAzn || 0);
  if (!(amount > 0)) throw new Error("Ödəniş məbləği düzgün deyil.");

  let resp;
  try {
    const r = await epx.post("/widget-url", {
      public_key: cfg.public_key.trim(),
      private_key: cfg.private_key.trim(),
      amount: +amount.toFixed(2),
      order_id: String(orderId),
      description: (description || `Sifariş ${orderId}`).slice(0, 250),
    });
    resp = r.data || {};
  } catch (e) {
    throw unwrapError(e, "Widget URL alınmadı");
  }

  if (resp.status !== "success" || !resp.widget_url) {
    throw new Error(resp.message || "Widget URL alınmadı");
  }
  return resp.widget_url;
}

/**
 * Server-to-server status check for a transaction or order_id.
 * Useful to reconcile after the user comes back to /success.
 */
export async function getEpointPaymentStatus({ transaction, orderId }) {
  const cfg = await readEpointConfig();

  let resp;
  try {
    const r = await epx.post("/get-status", {
      public_key: cfg.public_key.trim(),
      private_key: cfg.private_key.trim(),
      transaction: transaction || undefined,
      order_id: orderId ? String(orderId) : undefined,
    });
    resp = r.data || {};
  } catch (e) {
    throw unwrapError(e, "Epoint status sorğusu uğursuz oldu");
  }
  return resp;
}

/**
 * Verify a (data, signature) pair that Epoint sent back via redirect or
 * result_url. Tries the backend first (canonical), then falls back to a
 * local SHA-1 check using the private key from Firestore.
 */
export async function verifyRedirectPayload(data, signature) {
  try {
    const cfg = await getEpointConfig();
    if (cfg?.private_key) {
      const r = await epx.post("/verify-callback", {
        private_key: cfg.private_key.trim(),
        data,
        signature,
      });
      const body = r.data || {};
      if (body.valid && body.payload) return body.payload;
      if (body.valid === false) return null;
    }
  } catch {
    /* fall through to local verification */
  }

  try {
    const cfg = await getEpointConfig();
    if (cfg?.private_key) {
      const ok = await verifySignature(cfg.private_key, data, signature);
      if (!ok) return null;
    }
    return decodeEpointData(data);
  } catch {
    return null;
  }
}

/**
 * High-level helper used from CheckoutPage. Redirects the browser to Epoint's
 * hosted page. Tries top-level navigation first so Apple Pay / Google Pay can
 * render correctly even when the app is loaded inside an iframe wrapper
 * (e.g. the Emergent preview).
 */
export async function startEpointCheckout({ trackingNo, amountAzn, description, language }) {
  const cfg = await readEpointConfig();
  const base = getRedirectBase(cfg);
  const successUrl = `${base}/success?order_id=${encodeURIComponent(trackingNo)}`;
  const errorUrl = `${base}/error?order_id=${encodeURIComponent(trackingNo)}`;

  const { redirect_url, transaction } = await createEpointPayment({
    orderId: trackingNo,
    amountAzn,
    description,
    language,
    successRedirect: successUrl,
    errorRedirect: errorUrl,
    cfg,
  });

  if (transaction) {
    try {
      localStorage.setItem(`epoint_txn_${trackingNo}`, transaction);
    } catch {
      /* quota */
    }
  }

  // Top-level navigation so the bank page can use Apple Pay / Google Pay
  // (escape any iframe wrapper such as the preview).
  try {
    if (typeof window !== "undefined" && window.top && window.top !== window.self) {
      window.top.location.href = redirect_url;
      return;
    }
  } catch {
    /* cross-origin top — fall through to same-window nav */
  }
  window.location.assign(redirect_url);
}

export { DEFAULT_DOMAIN as EPOINT_SITE_DOMAIN };
